<script setup lang="ts">
  import { useToast } from '@solfacil/girassol';

  import { useListQueryFilters } from '~/composables/useListQueryFilters';
  import {
    useCreateOrderDeliveryInvoicesMutation,
    useCreateOrderInvoiceMutation,
    useGetOrderDetailsBackofficeQuery,
    type FindOrderOut,
    type GetOrderDetailsBackofficeQueryVariables,
    type OrderDelivery,
    type OrderInsurance,
  } from '~/types/ecommerce/generated.codegen';

  function orderInsurancesForKit(
    list: FindOrderOut['insurances'],
  ): OrderInsurance[] | undefined {
    const rows = (list ?? []).filter(
      (row): row is OrderInsurance => row != null,
    );
    if (rows.length === 0) return undefined;
    return [...rows].sort(
      (a, b) => (a.coverage_years ?? 0) - (b.coverage_years ?? 0),
    );
  }

  definePageMeta({
    middleware: ['pedidos'],
  });

  const { createErrorToast, createInformativeToast } = useToast();

  const route = useRoute('pedidos-id');
  const { getPedidosListPath } = useListQueryFilters();
  const pedidosListPath = getPedidosListPath();
  const order = ref<FindOrderOut>();
  const linkResolver = resolveComponent('nuxt-link') as any;
  const nfNotesOpen = ref(false);
  const trackingSection = ref<HTMLElement | null>(null);

  const getOrderDetailsBackofficeQueryVariables =
    ref<GetOrderDetailsBackofficeQueryVariables>({
      order_id: route.params.id as string,
    });
  const {
    onResult: getOrderByIdResult,
    onError: getOrderByIdError,
    loading: getOrderByIdLoading,
  } = useGetOrderDetailsBackofficeQuery(
    getOrderDetailsBackofficeQueryVariables,
    {
      context: {
        fetchPolicy: 'network-only',
      },
    },
  );
  getOrderByIdResult(result => {
    if (result.data?.getOrderDetailsBackoffice) {
      order.value = result.data.getOrderDetailsBackoffice as FindOrderOut;
      nextTick(() => {
        if (route.hash !== '#rastreamento') return;
        trackingSection.value?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }
  });

  getOrderByIdError(() => {
    if (!route.params.id) return;
    createErrorToast({
      title: 'Oops..',
      description:
        'Não conseguimos encontrar o pedido que você está procurando. Tente novamente mais tarde.',
    });
    navigateTo(pedidosListPath);
  });

  const kitInsurances = computed(() =>
    orderInsurancesForKit(order.value?.insurances),
  );

  const timeline = computed(() => order.value?.timeline ?? []);
  const isFobOrder = computed(() => order.value?.delivery?.type === 'FOB');
  const deliveries = computed(
    () => (order.value?.deliveries ?? []) as OrderDelivery[],
  );

  const invoiceNotes = computed(() => {
    const raw = order.value?.notes?.invoice;
    if (typeof raw !== 'string') return '';
    return raw.trim();
  });

  const hasVendaNF = computed(() =>
    Boolean(
      order.value?.actions?.some(
        action => action?.id === 'BILLING_NOTE' && action.enabled,
      ),
    ),
  );
  const hasRemessaNF = computed(() =>
    Boolean(
      order.value?.actions?.some(
        action => action?.id === 'STOCK_NOTE' && action.enabled,
      ),
    ),
  );
  const nfMenuEnabled = computed(
    () => hasVendaNF.value || hasRemessaNF.value,
  );
  const hasMultipleDeliveries = computed(() => deliveries.value.length > 1);
  const remessaNfLabel = computed(() =>
    hasMultipleDeliveries.value
      ? 'Todas as NF-e de remessa'
      : 'NF-e de remessa',
  );

  function openInvoiceUrls(urls: Array<string | null | undefined>) {
    const validUrls = urls.filter((url): url is string => Boolean(url));
    if (!validUrls.length) {
      createErrorToast('Erro ao baixar a nota fiscal');
      return;
    }
    for (const url of validUrls) {
      navigateTo(url, { external: true, open: { target: '_blank' } });
    }
  }

  const {
    mutate: createOrderInvoice,
    onDone: orderInvoiceResult,
    onError: orderInvoiceError,
    loading: invoiceLoading,
  } = useCreateOrderInvoiceMutation();
  orderInvoiceResult(result => {
    openInvoiceUrls(
      (result.data?.createOrderInvoice?.invoices ?? []).map(
        invoice => invoice?.url,
      ),
    );
  });
  orderInvoiceError(() => createErrorToast('Erro ao baixar a nota fiscal'));

  const {
    mutate: createOrderDeliveryInvoices,
    onDone: deliveryInvoicesResult,
    onError: deliveryInvoicesError,
    loading: deliveryInvoicesLoading,
  } = useCreateOrderDeliveryInvoicesMutation();
  deliveryInvoicesResult(result => {
    openInvoiceUrls(
      (result.data?.createOrderDeliveryInvoices?.invoices ?? []).map(
        invoice => invoice?.url,
      ),
    );
  });
  deliveryInvoicesError(() =>
    createErrorToast('Erro ao baixar a nota fiscal'),
  );

  const invoicesLoading = computed(
    () => invoiceLoading.value || deliveryInvoicesLoading.value,
  );
  const nfActionsDisabled = computed(
    () => !nfMenuEnabled.value || invoicesLoading.value,
  );

  function downloadRemessaNF() {
    if (!hasRemessaNF.value || invoicesLoading.value) return;

    if (hasMultipleDeliveries.value) {
      createInformativeToast('Gerando link para as notas fiscais de remessa');
      createOrderDeliveryInvoices({ order_id: order.value?.id || '' });
      return;
    }

    createInformativeToast('Gerando link para a NF de remessa');
    createOrderInvoice({
      order_id: order.value?.id || '',
      only_base_invoice: false,
    });
  }

  function downloadShippingNf(delivery: OrderDelivery) {
    if (!delivery.id || invoicesLoading.value) return;
    createInformativeToast('Gerando link para o documento');
    createOrderInvoice({
      order_id: order.value?.id || '',
      delivery_id: delivery.id,
    });
  }

  function downloadVendaNF() {
    if (!hasVendaNF.value || invoicesLoading.value) return;
    createInformativeToast('Gerando link para a NF de venda');
    createOrderInvoice({
      order_id: order.value?.id || '',
      only_base_invoice: true,
    });
  }

  const pageAlert = computed(() => {
    const step = timeline.value.find(
      s => s?.error || s?.warning || s?.informative,
    );
    if (!step) return null;
    const feedback: 'error' | 'warning' | 'informative' = step.error
      ? 'error'
      : step.warning
        ? 'warning'
        : 'informative';

    const cancelMsg = {
      title: 'Recebemos o seu pedido de cancelamento',
      description:
        'Em breve, o cancelamento será concluído e atualizaremos o status desse pedido.',
    };
    const alertConfigs: Record<
      string,
      Record<string, { title: string; description?: string }>
    > = {
      error: {
        CANCELLED: { title: 'Motivo do cancelamento' },
        RETURN: { title: 'Pedido devolvido' },
        REPPROVED: { title: 'Motivo da reprova' },
        EXPIRED: { title: 'Pedido expirado' },
      },
      warning: {
        ANALYSIS: cancelMsg,
        WAITING_PAYMENT: cancelMsg,
        WAITING_SEPARATION: cancelMsg,
      },
      informative: {
        ANALYSIS: { title: 'Preparando pagamento' },
        WAITING_PAYMENT: { title: 'Preparando pagamento' },
      },
    };
    const cfg = alertConfigs[feedback]?.[step.status ?? ''];
    if (!cfg && !step.title) return null;

    return {
      title: step.title || cfg!.title,
      description: step.reason || cfg?.description || step.message,
      feedback,
    };
  });
</script>

<template>
  <AppPage class="detail-page-content" :loading="getOrderByIdLoading">
    <SolBreadcrumb
      id="breadcrumb"
      :items="[
        { text: 'Pedidos', href: pedidosListPath, target: '_self' },
        { text: `Pedido: ${order?.client_name}`, href: '', target: '_self' },
      ]"
      :link-tag="linkResolver" />

    <section v-if="order" class="details-content">
      <OrdersTitleOrderDetail
        :client-name="order?.client_name || ''"
        :order-number="order?.number || 0"
        :innova-id="order?.innova_id || undefined"
        :order-actions="order?.actions"
        :order-id="order?.id || ''"
        :payment-method="order?.payment_method || ''"
        :can-download-sales-invoice="nfMenuEnabled && hasVendaNF"
        :can-download-shipping-invoices="nfMenuEnabled && hasRemessaNF"
        :has-multiple-deliveries="hasMultipleDeliveries"
        @download-sales-invoice="downloadVendaNF"
        @download-shipping-invoices="downloadRemessaNF" />
      <OrdersIntegratorCard :order="order" />

      <SolAlert
        v-if="pageAlert"
        id="order-alert-bo"
        :title="pageAlert.title"
        :feedback="pageAlert.feedback">
        <div v-if="pageAlert.description">
          {{ pageAlert.description }}
        </div>
      </SolAlert>

      <div class="order-b-columns">
        <div class="order-deliveries-section">
          <div class="order-deliveries-section__card">
            <OrdersStepsOrderDetail
              :order-steps="timeline"
              :pre-sale="order.pre_sale"
              :delivery-estimate="order.delivery_estimate"
              :schedule-pickup-text="order.schedule_pickup_text" />

            <div
              id="rastreamento"
              ref="trackingSection"
              class="order-deliveries-section__header">
              <h2 class="order-deliveries-section__title">
                <icon-girassol:local-shipping
                  class="order-deliveries-section__icon" />
                {{ order.deliveries_title }}
              </h2>
              <div class="order-deliveries-section__actions">
                <SolMenu id="notas-fiscais-menu" class="notas-fiscais-menu">
                  <template #trigger="{ toggle, isOpen }">
                    <button
                      type="button"
                      class="notas-fiscais-menu__trigger sol-button-core -tertiary -small"
                      :disabled="nfActionsDisabled"
                      :class="{ '-disabled': nfActionsDisabled }"
                      :aria-expanded="isOpen"
                      aria-haspopup="true"
                      @click="toggle()">
                      Notas fiscais
                      <icon-girassol:expand-more
                        class="notas-fiscais-menu__chevron" />
                    </button>
                  </template>
                  <SolMenuItem
                    id="nf-venda-menu-item"
                    :class="{ '-disabled': !hasVendaNF || invoicesLoading }"
                    @click="downloadVendaNF()">
                    <div class="nf-menu-item">
                      <icon-girassol:download class="nf-menu-item__icon" />
                      <span>NF-e de venda</span>
                    </div>
                  </SolMenuItem>
                  <SolMenuItem
                    id="nf-remessa-menu-item"
                    :class="{ '-disabled': !hasRemessaNF || invoicesLoading }"
                    @click="downloadRemessaNF()">
                    <div class="nf-menu-item">
                      <icon-girassol:download class="nf-menu-item__icon" />
                      <span>{{ remessaNfLabel }}</span>
                    </div>
                  </SolMenuItem>
                </SolMenu>
              </div>
            </div>

            <OrdersDeliveryCard
              v-for="delivery in deliveries"
              :key="delivery.id"
              :delivery="delivery"
              :is-fob="isFobOrder"
              :pending-payment="order.pending_payment"
              :partial-invoicing="order.partially_invoiced"
              :available-at-date="order.available_at_date"
              :schedule-pickup-text="order.schedule_pickup_text"
              @download-shipping="downloadShippingNf(delivery)" />

            <div class="nf-notes-row" :class="{ '--open': nfNotesOpen }">
              <button
                type="button"
                class="nf-notes-row__header"
                :aria-expanded="nfNotesOpen"
                @click="nfNotesOpen = !nfNotesOpen">
                <span class="nf-notes-row__label"
                  >Observações da Nota Fiscal</span
                >
                <icon-girassol:expand-more
                  v-if="!nfNotesOpen"
                  class="nf-notes-row__chevron" />
                <icon-girassol:expand-less
                  v-else
                  class="nf-notes-row__chevron" />
              </button>
              <div v-show="nfNotesOpen" class="nf-notes-row__body">
                <SolInputTextarea
                  v-if="invoiceNotes"
                  id="nf-notes-global"
                  :value="invoiceNotes"
                  name="nf-notes-global"
                  label=""
                  disabled />
                <p v-else class="nf-notes-row__empty">
                  Nota fiscal sem observações adicionais
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="order-b-columns__delivery-details">
          <OrdersShippingDetailCard :order="order" />
        </div>
      </div>

      <div class="kit-and-payment">
        <OrdersKitDetailsCard
          class="kit-column"
          :order="order"
          :insurances="kitInsurances" />
        <OrdersPaymentMethodCard :order="order" />
      </div>
      <OrdersDocumentsCard :order="order" />
      <OrdersInvoiceDataCard :order="order" />
    </section>
  </AppPage>
</template>

<style scoped lang="scss">
  .detail-page-content {
    @apply flex flex-col gap-2xs px-micro pt-4xs pb-xs md:site:p-2xs md:site:pb-xs;

    .details-content {
      @apply flex flex-col gap-2xs;

      > .kit-and-payment {
        @apply flex flex-col gap-2xs md:site:flex-row lg:site:flex-row;

        .kit-column {
          @apply min-w-0 flex-1;
        }
      }
    }
  }

  .order-b-columns {
    @apply flex flex-col gap-2xs lg:site:flex-row lg:site:items-start;

    > .order-deliveries-section {
      @apply min-w-0 w-full lg:site:flex-1;
    }

    > .order-b-columns__delivery-details {
      @apply w-full shrink-0 lg:site:w-[420px];
    }
  }

  .order-deliveries-section {
    @apply flex w-full min-w-0 flex-col gap-micro;

    &__header {
      @apply relative flex flex-col items-start gap-micro;
      @apply px-2xs pb-4xs pt-0;
      @apply sm:site:flex-row sm:site:flex-wrap sm:site:items-center sm:site:justify-between;
      scroll-margin-top: 96px;

      &::after {
        content: '';
        @apply absolute bottom-0 left-2xs right-2xs h-px bg-neutral-high-medium;
      }
    }

    &__actions {
      @apply flex flex-row flex-wrap items-center gap-micro;
    }

    .notas-fiscais-menu {
      @apply relative z-10;

      &__trigger {
        @apply flex cursor-pointer flex-row items-center gap-quark;

        &.-disabled,
        &:disabled {
          @apply pointer-events-none cursor-default opacity-50;
        }
      }

      &__chevron {
        @apply icon-size-small;
      }
    }

    .nf-menu-item {
      @apply flex items-center gap-nano;

      &__icon {
        @apply icon-size-small;
      }
    }

    &__title {
      @apply fonts-body-large-bold flex flex-row items-center gap-nano text-neutral-low-dark;
    }

    &__icon {
      @apply icon-size-medium text-brand-secondary-pure;
    }

    &__card {
      @apply min-w-0 overflow-hidden rounded-lg border border-neutral-high-medium bg-neutral-high-pure;

      > .delivery-card + .delivery-card,
      > .delivery-card + .nf-notes-row {
        @apply relative;

        &::before {
          content: '';
          @apply absolute left-4xs right-4xs top-0 h-px bg-neutral-high-medium md:site:left-2xs md:site:right-2xs;
        }
      }
    }
  }

  .nf-notes-row {
    @apply bg-neutral-high-pure;

    &.--open &__header {
      @apply pb-4xs;
    }

    &__header {
      @apply flex w-full flex-row items-center justify-between;
      @apply cursor-pointer select-none px-2xs pb-2xs pt-4xs text-left;

      &:focus-visible {
        @apply outline-2 outline-offset-2 outline-brand-secondary-pure;
      }
    }

    &__label {
      @apply fonts-body-large-bold text-neutral-low-dark;
    }

    &__chevron {
      @apply icon-size-small shrink-0 text-neutral-low-light;
    }

    &__body {
      @apply px-2xs pb-2xs;
    }

    &__empty {
      @apply fonts-body-medium-regular text-neutral-low-light;
    }
  }
</style>
